import { CheckedAddress, Hash256, UnixDuration } from '@sparkdotfi/common-universal'
import { HttpResponse, http } from 'msw'
import type { SetupWorker } from 'msw/browser'
import { mergeDeep } from 'remeda'
import { DeepPartial } from 'ts-essentials'
import { concat, pad, slice, toHex } from 'viem'
import { mainnet } from 'viem/chains'

import { TestnetClient } from '../tenderly/TenderlyClient'
import { spark2ApiUrl } from '@/config/consts'
import { spkStakingModuleAddress } from '@/config/contracts-generated'
import { getContractAddress } from '@/domain/hooks/useContractAddress'

// Default mock data
const DEFAULT_WALLET_DATA = {
  slashable_balance_normalized: '0',
  points_normalized: '0',
  points_per_second_normalized: '0',
  timestamp_updated: Math.ceil(Date.now() / 1000),
}

const DEFAULT_STATS_DATA = {
  tvl: '5630000',
  number_of_wallets: 78,
}

export interface SpkStakingEndpointsConfig {
  walletData: typeof DEFAULT_WALLET_DATA
  statsData: typeof DEFAULT_STATS_DATA
  account: CheckedAddress
}

let currentConfig: SpkStakingEndpointsConfig = {
  walletData: DEFAULT_WALLET_DATA,
  statsData: DEFAULT_STATS_DATA,
  account: CheckedAddress.random(),
} // global state, yes

export interface SetupSpkStakingParams {
  msw: SetupWorker
  testnetClient: TestnetClient
  account: CheckedAddress
}

export async function setupSpkStaking({ msw, testnetClient, account }: SetupSpkStakingParams): Promise<void> {
  await setEpochDurationStorage(
    testnetClient,
    getContractAddress(spkStakingModuleAddress, mainnet.id),
    Number(UnixDuration({ seconds: 10 }).toSeconds()),
  )

  currentConfig = {
    walletData: DEFAULT_WALLET_DATA,
    statsData: DEFAULT_STATS_DATA,
    account,
  }

  msw.use(
    http.get(`${spark2ApiUrl}/spk-staking/${account}/`, () => {
      return HttpResponse.json(currentConfig.walletData)
    }),
  )
}

export function getEndpointsConfig(): SpkStakingEndpointsConfig {
  return currentConfig
}

export function updateEndpoints(msw: SetupWorker, newConfig: DeepPartial<SpkStakingEndpointsConfig>): void {
  currentConfig = mergeDeep(currentConfig, newConfig) as SpkStakingEndpointsConfig

  msw.use(
    http.get(`${spark2ApiUrl}/spk-staking/${currentConfig.account}/`, () => {
      return HttpResponse.json(currentConfig.walletData)
    }),
    http.get(`${spark2ApiUrl}/spk-staking/stats/`, () => {
      return HttpResponse.json(currentConfig.statsData)
    }),
  )
}

async function setEpochDurationStorage(
  testnetClient: TestnetClient,
  spkStakingVaultAddress: CheckedAddress,
  newEpochDuration: number,
): Promise<void> {
  const VAULT_TOKENIZED_SLOT_EPOCH_DURATION = 1n
  const EPOCH_DURATION_OFFSET_BYTES = 26
  const EPOCH_DURATION_SIZE_BYTES = 6
  const SLOT_SIZE_BYTES = 32

  const slotIndexHex = Hash256(pad(toHex(VAULT_TOKENIZED_SLOT_EPOCH_DURATION), { size: SLOT_SIZE_BYTES }))

  const oldStorageValue = await testnetClient.getStorageAt({
    address: spkStakingVaultAddress,
    slot: slotIndexHex,
  })

  if (!oldStorageValue) {
    throw new Error('Failed to read storage slot')
  }

  const encodedDuration = pad(toHex(newEpochDuration), { size: EPOCH_DURATION_SIZE_BYTES })
  const newStorageValue = concat([
    encodedDuration,
    slice(oldStorageValue, SLOT_SIZE_BYTES - EPOCH_DURATION_OFFSET_BYTES),
  ])

  await testnetClient.setStorageAt(spkStakingVaultAddress, slotIndexHex, newStorageValue)
}
